import React from 'react';
import { IonChip, IonLabel, IonButton, IonIcon, IonItem, IonInput, IonTextarea, IonModal, IonHeader, IonToolbar, IonTitle, IonContent } from '@ionic/react';
import { create, close } from 'ionicons/icons';
import { StrategyRecommendationTable } from '../parameters-schema';
import DecisionTreeTable from '../shared/decision-tree-table';
import { 
  formatDecision, 
  getColumnDisplayName 
} from '../shared/decision-tree-utils';
import { extractStrategyRecommendationPaths, padPathsToColumnCount } from '../shared/path-extraction';
import { useDecisionTreeEditor } from '../shared/use-decision-tree-editor';
import StrategyRecommendationLeafEditor from './leaf-output-editor';
import { useTags } from '../../tags/tags-provider';
import { useParameters } from '../parameters-provider';
import ColumnSelector from '../shared/column-selector';

interface EnhancedStrategyRecommendationEditorProps {
  table: StrategyRecommendationTable;
  onUpdate: (newTable: StrategyRecommendationTable) => void;
}

const EnhancedStrategyRecommendationEditor: React.FC<EnhancedStrategyRecommendationEditorProps> = ({
  table,
  onUpdate
}) => {
  const { tags } = useTags();
  const { parameters } = useParameters();
  
  // Use shared decision tree editor hook
  const {
    isLeafEditorOpen,
    editingOutput,
    isColumnModalOpen,
    paths,
    handleEditOutput,
    handleSaveOutput,
    handleCreateSplit,
    handleEditSplit,
    handleDeleteBranch,
    handleAddColumn,
    handleDeleteColumn,
    handleAddNewColumn,
    closeLeafEditor,
    closeColumnModal
  } = useDecisionTreeEditor({
    table,
    inputColumns: table.inputColumns,
    decisionTree: table.decisionTree,
    extractPaths: (tree) => {
      const extractedPaths = extractStrategyRecommendationPaths(tree);
      return padPathsToColumnCount(extractedPaths, table.inputColumns.length);
    },
    onUpdate
  });

  // Format decision for display
  const formatDecisionDisplay = (decision: any, column?: any) => {
    return formatDecision(decision, column);
  };

  // Render output cell for strategy recommendations
  const renderOutputCell = (output: any, rowIndex: number) => {
    const strategyMenu = output.strategyMenu || [];
    const extraTags = output.extraTags || [];
    
    // Get actual tag names
    const productTags = tags?.filter(tag => tag.isProduct === true) || [];
    const nonProductTags = tags?.filter(tag => tag.isProduct === false) || [];
    
    return (
      <div style={{ 
        display: 'flex', 
        alignItems: 'flex-start', 
        justifyContent: 'space-between',
        padding: '0.5rem',
        minHeight: '60px'
      }}>
        <div style={{ flex: 1, paddingRight: '0.5rem' }}>
          {/* Strategy Menu */}
          <div style={{ marginBottom: '0.75rem' }}>
            <strong style={{ display: 'block', marginBottom: '0.5rem' }}>Strategy Menu:</strong>
            {strategyMenu.length > 0 ? (
              <div style={{ 
                display: 'flex', 
                flexWrap: 'wrap', 
                gap: '0.5rem',
                alignItems: 'center'
              }}>
                {strategyMenu.slice(0, 3).map((tagId: string, index: number) => {
                  const tag = productTags.find(t => t.id === tagId);
                  return (
                    <IonChip key={index} color="primary" style={{ margin: 0 }}>
                      <IonLabel style={{ fontSize: '0.8em' }}>
                        {tag?.displayName || `Product ${index + 1}`}
                      </IonLabel>
                    </IonChip>
                  );
                })}
                {strategyMenu.length > 3 && (
                  <IonChip color="medium" style={{ margin: 0 }}>
                    <IonLabel style={{ fontSize: '0.8em' }}>
                      +{strategyMenu.length - 3} more
                    </IonLabel>
                  </IonChip>
                )}
              </div>
            ) : (
              <span style={{ 
                color: 'var(--ion-color-medium)', 
                fontStyle: 'italic',
                fontSize: '0.9em'
              }}>No products</span>
            )}
          </div>
          
          {/* Explanation */}
          {output.explanation && (
            <div style={{ marginBottom: '0.75rem' }}>
              <strong style={{ display: 'block', marginBottom: '0.25rem' }}>Explanation:</strong>
              <div style={{ 
                fontSize: '0.85em', 
                color: 'var(--ion-color-medium)',
                lineHeight: '1.4',
                padding: '0.25rem 0'
              }}>
                {output.explanation.length > 100 
                  ? `${output.explanation.substring(0, 100)}...` 
                  : output.explanation
                }
              </div>
            </div>
          )}
          
          {/* Extra Tags */}
          {extraTags.length > 0 && (
            <div>
              <strong style={{ display: 'block', marginBottom: '0.5rem' }}>Extra Tags:</strong>
              <div style={{ 
                display: 'flex', 
                flexWrap: 'wrap', 
                gap: '0.5rem',
                alignItems: 'center'
              }}>
                {extraTags.slice(0, 3).map((tagId: string, index: number) => {
                  const tag = nonProductTags.find(t => t.id === tagId);
                  return (
                    <IonChip key={index} color="secondary" style={{ margin: 0 }}>
                      <IonLabel style={{ fontSize: '0.8em' }}>
                        {tag?.displayName || `Tag ${index + 1}`}
                      </IonLabel>
                    </IonChip>
                  );
                })}
                {extraTags.length > 3 && (
                  <IonChip color="medium" style={{ margin: 0 }}>
                    <IonLabel style={{ fontSize: '0.8em' }}>
                      +{extraTags.length - 3} more
                    </IonLabel>
                  </IonChip>
                )}
              </div>
            </div>
          )}
        </div>
        
        {/* Edit Button */}
        <IonButton
          fill="clear"
          size="small"
          style={{ 
            alignSelf: 'flex-start',
            marginTop: '0.25rem',
            minWidth: '32px',
            height: '32px'
          }}
          onClick={(e) => {
            e.stopPropagation();
            handleEditOutput(rowIndex, output);
          }}
        >
          <IonIcon icon={create} />
        </IonButton>
      </div>
    );
  };


  return (
    <div className="enhanced-strategy-recommendation-editor">
      {/* Title and Description */}
      <IonItem>
        <IonLabel position="stacked">Title</IonLabel>
        <IonInput
          value={table.displayTitle}
          onIonChange={e => onUpdate({ ...table, displayTitle: e.detail.value || undefined })}
          placeholder="Enter a title for this strategy table"
        />
      </IonItem>
      
      <IonItem>
        <IonLabel position="stacked">Description</IonLabel>
        <IonTextarea
          value={table.parameterDescription}
          onIonChange={e => onUpdate({ ...table, parameterDescription: e.detail.value || undefined })}
          placeholder="Describe the purpose of this strategy table"
        />
      </IonItem>

      <DecisionTreeTable
        table={table}
        onUpdate={onUpdate}
        getColumnDisplayName={(column) => getColumnDisplayName(column, tags || [], parameters || [])}
        formatDecision={formatDecisionDisplay}
        renderOutputCell={renderOutputCell}
        onEditOutput={handleEditOutput}
        onCreateSplit={handleCreateSplit}
        onEditSplit={handleEditSplit}
        onDeleteBranch={handleDeleteBranch}
        onAddColumn={handleAddColumn}
        onDeleteColumn={handleDeleteColumn}
        paths={paths}
      />

      <StrategyRecommendationLeafEditor
        output={editingOutput || { strategyMenu: [], explanation: '', extraTags: [] }}
        onUpdate={handleSaveOutput}
        isOpen={isLeafEditorOpen}
        onClose={closeLeafEditor}
      />

      {/* Column Selection Modal */}
      <IonModal isOpen={isColumnModalOpen} onDidDismiss={closeColumnModal}>
        <IonHeader>
          <IonToolbar>
            <IonTitle>Add Column</IonTitle>
            <IonButton
              fill="clear"
              slot="end"
              onClick={closeColumnModal}
            >
              <IonIcon icon={close} />
            </IonButton>
          </IonToolbar>
        </IonHeader>
        <IonContent>
          <ColumnSelector
            onAddColumn={handleAddNewColumn}
            existingColumns={table.inputColumns}
            showTagsAndLightStatus={true}
          />
        </IonContent>
      </IonModal>

      <style>{`
        .enhanced-strategy-recommendation-editor {
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }
      `}</style>
    </div>
  );
};

export default EnhancedStrategyRecommendationEditor;
